import React, { useContext, useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import useWebSocket from 'react-use-websocket';
import MicRecorder from 'mic-recorder-to-mp3';
import { AppSettings } from './../../config/app-settings.js';
import AIChatbotTopicData from '../../assets/data/aichatbot/topics.json';
import Config from '../config.json';
import defaultGaleAvatar from '../../assets/img/aichatbot/gale_profile_1.jpg';
import defaultPersonAvatar from '../../assets/img/aichatbot/steering_wheel_1.jpg';
import { themes } from './themes.js';
import '../../custom.css';


const Mp3Recorder = new MicRecorder({ bitRate: 128 });
const WS_URL = 'wss://6aiolymuhf.execute-api.us-east-2.amazonaws.com/prod';

function AIChatbot() {
    const [chatWith, setChatWith] = useState('');
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [pageHeader, setPageHeader] = useState(null);
    const [showProgress, setShowProgress] = useState(true);
    const [submitDisabled, setSubmitDisabled] = useState(true);
    const [collapsedThinking, setCollapsedThinking] = useState({});
    const [textareaHeight, setTextareaHeight] = useState(48);

    const [isRecording, setIsRecording] = useState(false);
    const [output, setOutput] = useState(null);
    const [galeAvatar, setGaleAvatar] = useState(defaultGaleAvatar);
    const [personAvatar, setPersonAvatar] = useState(defaultPersonAvatar);
    const [avatarsLoaded, setAvatarsLoaded] = useState(false);
    const [showAvatarModal, setShowAvatarModal] = useState(false);
    const [modalAvatarSrc, setModalAvatarSrc] = useState('');
    const [pageContent, setPageContent] = useState(null);
    const [isAIStreaming, setIsAIStreaming] = useState(false);
    const [isInIframe] = useState(() => {
        // Method 1: Check if in iframe
        const inIframe = window.self !== window.top;
        
        // Method 2: Check URL parameter
        const urlParams = new URLSearchParams(window.location.search);
        const isWidget = urlParams.get('widget') === 'true';
        
        // Method 3: Check if parent exists and is different from window
        const hasParent = window.parent && window.parent !== window;
        
        // Method 4: Check document referrer for widget pattern
        const referrerIsWidget = document.referrer && document.referrer.includes('dealership');
        
        return inIframe || isWidget || hasParent || referrerIsWidget;
    });

    const context = useContext(AppSettings);
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);
    const waitingTimeoutRef = useRef(null);
    const initialRequestSentRef = useRef(false);
    const { topic } = useParams();
    // Treat non-root paths as non-home by default. Maintain an allowlist of first-segment keywords
    // that should still count as home when path depth is shallow (e.g., '/dealership/<slug>').
    const HOME_KEYWORDS = [
        'dealership' // add more here as needed, e.g., 'home', 'index'
    ];

    const isParentHomeUrl = (url) => {
        try {
            const u = new URL(url);
            const segments = u.pathname.split('/').filter(Boolean);
            if (segments.length === 0) return true; // '/'
            const first = (segments[0] || '').toLowerCase();
            if (HOME_KEYWORDS.includes(first) && segments.length <= 2) return true;
            return false; // default non-home
        } catch {
            return true;
        }
    };


    // Centralized allowlist for dealership domains (keep this small and focused)
    const allowedOrigins = [
        'https://creditsolutionautosales.com',
        'https://www.creditsolutionautosales.com',
        'https://myautoamerica.com', 
        'https://www.myautoamerica.com',
        'https://galeforceai.com',
        'https://www.galeforceai.com'
    ];

    const isAllowedOrigin = (origin) => {
        return allowedOrigins.includes(origin) || 
               origin.includes('localhost') || 
               origin.includes('127.0.0.1');
    };

    const getChatTopicData = () => {
        if (topic.includes('-')) {
            // const parts = topic.split('-');  // Split the text by the hyphens
            // const team_id = parts.slice(0, 3).join('-');  // Join the first three parts for the team_id.
            // const contact_id = parts[3];  // contact_id

            return AIChatbotTopicData['socrates'];
        } else if (AIChatbotTopicData[topic]) {
            return AIChatbotTopicData[topic];
        } else {
            return AIChatbotTopicData['default'];
        }

        const firstTwoChars = topic.slice(0, 2).toUpperCase();

        if (firstTwoChars === 'ZH') {
            return AIChatbotTopicData['socrates'];
        } else if (AIChatbotTopicData[topic]) {
            return AIChatbotTopicData[topic];
        } else {
            return AIChatbotTopicData['default'];
        }
    }

    const chatTopicData = getChatTopicData();
    
    // Theme selection with multiple safety layers
    const theme = themes[chatTopicData?.design] || themes.default || {
        // Ultimate fallback if themes object is broken
        gradient: 'linear-gradient(to right, #00ffff 0%, #ff00ff 15%, #ed521f 100%)',
        colors: { primary: '#525252', secondary: '#4a4a4a', text: 'white', inputText: 'white' },
        layout: { borderRadius: '18px', avatarRadius: '6px', selectRadius: '18px' },
        typography: { fontFamily: 'Arial, sans-serif', headerSize: '18px', bodySize: '15px' },
        background: null
    };
    
    if (!localStorage.getItem('user_id')) {
        localStorage.setItem(
            'user_id', 
            `${topic}-${Intl.DateTimeFormat().resolvedOptions().timeZone}-${window.screen.width}x${window.screen.height}-${Math.floor(Math.random() * 10000) + 1}`
        );
    }

    let userId = localStorage.getItem('user_id');
    
    // Function to retrieve conversation history from localStorage
    const getStoredConversationHistory = () => {
        try {
            const storageKey = `conversation_${userId}`;
            const stored = localStorage.getItem(storageKey);
            
            if (stored) {
                const parsedHistory = JSON.parse(stored);
                // Validate the structure
                if (Array.isArray(parsedHistory) && parsedHistory.length > 0) {
                    return parsedHistory;
                }
            }
        } catch (error) {
            console.log('Failed to retrieve conversation history:', error);
        }
        return null;
    };
    
    const { getWebSocket } = useWebSocket(WS_URL + '?sourceSystemUrl=' + window.location.href + '&userId=' + userId, {
        onOpen: () => {
            setSubmitDisabled(false);
        },
        onMessage: (event) => {
            // Destructure the event_type and message from the parsed event.data
            const message = JSON.parse(event.data).message;
            const event_type = JSON.parse(event.data).event_type;

            // Detect streaming completion
            if (message === 'llm streaming complete') {
                setIsAIStreaming(false);  // Mark streaming as complete
                return;
            }

            setMessages((prevMessages) => {
                // Create a copy of the previous messages array
                const updatedMessages = [...prevMessages];

                if (event_type === "new") {
                    // Processing new message event

                    // If event_type is "new", create a new message object for Gale
                    updatedMessages.push({
                        id: Date.now(),
                        text: message,
                        sender: 'Gale',
                        avatar: galeAvatar,
                    });

                    // Messages array has been updated
                } else {
                    // If event_type is not "new", find the last message from Gale
                    const lastGaleMessageIndex = updatedMessages.findLastIndex(
                        (msg) => msg.sender === 'Gale'
                    );

                    if (lastGaleMessageIndex >= 0) {
                        // If the last message from Gale exists
                        const currentMessage = updatedMessages[lastGaleMessageIndex];
                        const currentText = currentMessage.text || '';
                        const placeholders = [
                            chatTopicData.initial_message,
                            'Gale is reading your message...',
                            'Gale is now writing a response...',
                            'Gale is writing a response...',
                            'Gale is writing a message...'
                        ];

                        const shouldReplace = placeholders.includes(currentText) || (message && message.length > currentText.length);

                        if (shouldReplace) {
                            updatedMessages[lastGaleMessageIndex] = {
                                ...currentMessage,
                                text: message,
                            };
                        }
                    } else {
                        // If there are no previous messages from Gale, create a new message object
                        updatedMessages.push({
                        id: Date.now(),
                        text: message,
                        sender: 'Gale',
                        avatar: galeAvatar,
                        });
                    }
                }

                // Return the updated messages array
                return updatedMessages;
            });
        },
        shouldReconnect: (closeEvent) => true
    });

    const createInitialMessage = () => {
        document.title = chatTopicData.page_title;

        const systemMessage = {
            id: userId,
            text: 'User ID',
            sender: 'System',
            topic: topic,
            pageContext: pageContent || ''
        };

        // if (String(chatTopicData.page_title).includes('Sales AI Agent')) {
        if (String(topic).includes('gale-force-ai')) {
            const topicParts = topic.split('-');
            // Parsing topic to extract contact information
            // Topic contains hyphen-separated values
            // Last part is the contact ID

            fetch('https://1w0ddg9746.execute-api.us-east-2.amazonaws.com/contactdatarequest', {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Authorization': Config.contactdatarequest,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    'access_id': getAccessId(),
                    'chat_with': chatWith,
                    'contact_id': topicParts[topicParts.length - 1]
                })
            })
                .then((response) => response.json())
                .then((response) => {
                    // Successfully retrieved contact data
                    setPageHeader(chatTopicData.page_header + ' ' + response.first_name + ' ' + response.last_name);
                    let initialMessageText = chatWith;

                    if (chatWith === "CRM Notes" || chatWith === '') {
                        let contactNotes = response.notes.map((item, index) => `${index + 1}. ${item}`).join('\n');
                        initialMessageText = 
                            chatTopicData.initial_message.replace(new RegExp('\\$contact_name', 'g'), response.first_name) + '\n\n' + 
                            'Here are the CRM notes on ' + response.first_name + ':\n\n' + 
                            contactNotes;
                    } else if (chatWith === "Call") {
                        let business_calls = response.business_calls;
                        initialMessageText = 
                            chatTopicData.initial_message.replace(new RegExp('\\$contact_name', 'g'), response.first_name) + '\n\n' + 
                            'Here is the call transcript from ' + response.first_name + '\'s last call:\n\n' + 
                            business_calls[0].transcript;
                    }

                    const message = {
                        id: Date.now(),
                        text: initialMessageText,
                        sender: 'Gale',
                        avatar: galeAvatar
                    };

            setMessages([systemMessage, message]);
                })
                .catch(function (error) {
                    // Failed to fetch contact data
                    setSubmitDisabled(false);
                });
        } else {
            setPageHeader(chatTopicData.page_header);

            // If we already have page context, render the correct initial Gale message immediately.
            // Otherwise, avoid adding a Gale message yet to prevent a flash; we will add it once
            // pageContext arrives in the postMessage handler.
            if (pageContent) {
                const isHome = isParentHomeUrl(pageContent.url);
                const message = {
                    id: Date.now(),
                    text: isHome ? chatTopicData.initial_message : 'Gale is writing a message...',
                    sender: 'Gale',
                    avatar: galeAvatar
                };
                setMessages([systemMessage, message]);
            } else {
                setMessages([systemMessage]);
            }
        }  
    };

    const getAccessId = () => {
        return `${localStorage.getItem('user_id')}-${localStorage.getItem('optimus')}`;
    }

    const handleChatWithChange = (event) => {
        // User changed chat type selection
        setChatWith(event.target.value);
        // createInitialMessage();
    };

    const handleMessageChange = (e) => {
        setNewMessage(e.target.value);
        // Auto-resize the textarea
        adjustTextareaHeight(e.target);
    };

    const adjustTextareaHeight = (element) => {
        // Reset height to auto to accurately calculate the new height
        element.style.height = '48px';
        
        // Check if content exceeds the single line height
        const scrollHeight = element.scrollHeight;
        const isSingleLine = scrollHeight <= 48;
        
        // Only adjust height if content exceeds single line
        if (!isSingleLine) {
            element.style.height = `${scrollHeight}px`;
            setTextareaHeight(scrollHeight);
        } else {
            // Keep default height for single line
            setTextareaHeight(48);
        }
    };

    const handleSendMessage = () => {
        if (!newMessage.trim()) return; // Ignore empty messages.
        
        // Mark that AI is starting to stream
        setIsAIStreaming(true);
        
        const updatedMessages = [
            ...messages,
            {
                id: Date.now(),
                text: newMessage,
                sender: 'You',
                avatar: personAvatar
            },
            {
                id: Date.now() + 1,
                text: 'Gale is reading your message...',
                sender: 'Gale',
                avatar: galeAvatar
            }
        ];
        // Prepared messages for API request
        setMessages(updatedMessages);
        setNewMessage('');
        
        // Add conversationHistory to the System message before sending
        const messagesToSend = [...updatedMessages];
        const systemMessageIndex = messagesToSend.findIndex(msg => msg.sender === 'System');
        if (systemMessageIndex >= 0) {
            const conversationHistory = getStoredConversationHistory();
            messagesToSend[systemMessageIndex] = {
                ...messagesToSend[systemMessageIndex],
                conversationHistory: conversationHistory || []  // Send empty array instead of null
            };
        }
        
        // Clear any existing timeout
        if (waitingTimeoutRef.current) {
            clearTimeout(waitingTimeoutRef.current);
        }
        
        // Set timeout to change waiting message after 2 seconds
        waitingTimeoutRef.current = setTimeout(() => {
            setMessages(prevMessages => {
                const updatedMessages = [...prevMessages];
                const lastGaleIndex = updatedMessages.findLastIndex(msg => msg.sender === 'Gale');
                if (lastGaleIndex >= 0 && updatedMessages[lastGaleIndex].text === 'Gale is reading your message...') {
                    updatedMessages[lastGaleIndex] = {
                        ...updatedMessages[lastGaleIndex],
                        text: 'Gale is now writing a response...'
                    };
                }
                return updatedMessages;
            });
        }, 2000);
        
        fetch('https://3p5i2ef2f3.execute-api.us-east-2.amazonaws.com/aichatbotchatrequest', {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Authorization': Config.aichatbotchatrequest,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(messagesToSend)
        })
            .then((response) => response.json())
            .then((response) => {
                // Successfully received chat response
                setShowProgress(false);
            })
            .catch(function (err) {
                // Failed to send chat message
                setIsAIStreaming(false);  // Reset streaming state
                // Update the last Gale message to show error
                setMessages((prevMessages) => {
                    const updatedMessages = [...prevMessages];
                    const lastGaleIndex = updatedMessages.findLastIndex(
                        (msg) => msg.sender === 'Gale'
                    );
                    if (lastGaleIndex >= 0) {
                        updatedMessages[lastGaleIndex] = {
                            ...updatedMessages[lastGaleIndex],
                            text: 'Sorry, your message could not be sent. Please refresh the page and try again.'
                        };
                    }
                    return updatedMessages;
                });
                setSubmitDisabled(false); // Re-enable send button
            });
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const startRecording = () => {
        setIsRecording(true);
        Mp3Recorder.start()
            .then(() => {})
            .catch((error) => {
                // Failed to start voice recording
                alert('Error starting recording.');
            });
    };

    const stopRecording = async () => {
        setIsRecording(false);
        Mp3Recorder.stop()
            .getMp3()
            .then(([buffer, blob]) => {
                uploadAndTranscribe(blob);
            })
            .catch((error) => {
                // Failed to stop voice recording
                alert('Error stopping recording.');
            });
    };

    const uploadAndTranscribe = async (mp3Blob) => {
        const formData = new FormData();
        formData.append('audio', mp3Blob, 'recording.mp3');
        setOutput('Uploading audio recording...')

        try {
            const response = await fetch('https://xyr44fotj7.execute-api.us-east-2.amazonaws.com/callmentorrequest', {
                method: 'POST',
                headers: {
                    'Authorization': Config.callmentorrequest,
                    'Content-Type': 'multipart/form-data',
                    'X-Request-ID': requestId(),
                    'X-Original-Filename': 'recording.mp3',
                    'X-Topic': topic
                },
                body: formData,
            });

            const data = await response.json();
            setOutput('Audio file upload: ' + data.result)
        } catch (error) {
            // Failed to upload audio file
            setSubmitDisabled(false);
        }
    };

    const requestId = () => {
        return `${localStorage.getItem('user_id')}-${localStorage.getItem('optimus')}-text`;
    }

    // Removed the timed bootstrap effect; we now send bootstrap immediately when pageContext arrives

    const loadAvatars = async () => {
        try {
            const avatars = chatTopicData.avatars;
            let galeAvatarUrl = defaultGaleAvatar;
            let personAvatarUrl = defaultPersonAvatar;
            
            // Check if avatars array exists and has elements
            if (avatars && Array.isArray(avatars)) {
                // Load Gale avatar (first element)
                if (avatars[0]) {
                    try {
                        const galeAvatarModule = await import(`../../assets/img/aichatbot/${avatars[0]}`);
                        galeAvatarUrl = galeAvatarModule.default;
                    } catch (error) {
                        // Could not load custom Gale avatar, using default
                        // Keep default galeAvatar
                    }
                }
                
                // Load person avatar (second element)
                if (avatars[1]) {
                    try {
                        const personAvatarModule = await import(`../../assets/img/aichatbot/${avatars[1]}`);
                        personAvatarUrl = personAvatarModule.default;
                    } catch (error) {
                        // Could not load custom person avatar, using default
                        // Keep default personAvatar
                    }
                }
            }
            
            // Preload each image independently to ensure they're cached
            const preloadGale = new Promise((resolve) => {
                const img = new Image();
                img.onload = () => {
                    setGaleAvatar(galeAvatarUrl);
                    resolve();
                };
                img.onerror = () => {
                    // Gale avatar failed to load, using fallback
                    setGaleAvatar(defaultGaleAvatar);
                    resolve();
                };
                img.src = galeAvatarUrl;
            });
            
            const preloadPerson = new Promise((resolve) => {
                const img = new Image();
                img.onload = () => {
                    setPersonAvatar(personAvatarUrl);
                    resolve();
                };
                img.onerror = () => {
                    // Person avatar failed to load, using fallback
                    setPersonAvatar(defaultPersonAvatar);
                    resolve();
                };
                img.src = personAvatarUrl;
            });
            
            // Wait for both to complete (success or failure)
            await Promise.all([preloadGale, preloadPerson]);
            
        } catch (error) {
            // Avatar loading failed, using default images
            setGaleAvatar(defaultGaleAvatar);
            setPersonAvatar(defaultPersonAvatar);
        } finally {
            setAvatarsLoaded(true);
        }
    }

    const toggleThinking = (messageId) => {
        setCollapsedThinking(prev => {
            // Get the current state (default to true/collapsed if undefined)
            const currentState = prev[messageId] !== undefined ? prev[messageId] : true;
            
            return {
                ...prev,
                [messageId]: !currentState
            };
        });
    };

    const handleAvatarClick = (avatarSrc) => {
        setModalAvatarSrc(avatarSrc);
        setShowAvatarModal(true);
    };

    const handleModalClose = (e) => {
        if (e.target === e.currentTarget) {
            setShowAvatarModal(false);
        }
    };
    
    // Simple, stateless typing dots indicator (UI-only)
    const TypingDots = () => (
        <span className="gfa-typing-dots" aria-live="polite" aria-label="typing">
            <span className="gfa-typing-dot"></span>
            <span className="gfa-typing-dot"></span>
            <span className="gfa-typing-dot"></span>
        </span>
    );
    
    // Minimal fallback: ensure UI isn't blank and backend is called if pageContext postMessage was missed
    useEffect(() => {
        const timer = setTimeout(() => {
            if (initialRequestSentRef.current) return;

            // Insert a single typing message if none exists
            setMessages(prev => {
                const hasGale = prev.some(m => m.sender === 'Gale');
                if (hasGale) return prev;
                return [
                    ...prev,
                    { id: Date.now(), text: 'Gale is writing a message...', sender: 'Gale', avatar: galeAvatar }
                ];
            });

            // Send bootstrap once (with stub context if needed)
            try {
                // Claim the bootstrap send synchronously to avoid duplicates
                initialRequestSentRef.current = true;
                // Mark that AI is starting to stream
                setIsAIStreaming(true);
                const fallbackContext = {
                    type: 'pageContent',
                    url: document.referrer || window.location.href,
                    title: document.title || '',
                    description: '',
                    mainText: '',
                    headings: [],
                    selectedText: '',
                    timestamp: Date.now()
                };
                const conversationHistory = getStoredConversationHistory();
                const bootstrapSystemMessage = {
                    id: userId,
                    text: 'User ID',
                    sender: 'System',
                    topic: topic,
                    pageContext: pageContent || fallbackContext,
                    conversationHistory: conversationHistory || [],  // Send empty array instead of null
                    purpose: 'bootstrap'
                };
                fetch('https://3p5i2ef2f3.execute-api.us-east-2.amazonaws.com/aichatbotchatrequest', {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Authorization': Config.aichatbotchatrequest,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify([bootstrapSystemMessage])
                }).catch(() => {
                    setIsAIStreaming(false);  // Reset on error
                });
            } catch (_) {
                setIsAIStreaming(false);  // Reset on error
                // keep flag set; do not cascade
            }
        }, 850);

        return () => clearTimeout(timer);
    }, []);
    
    const renderMessageText = (message) => {
        // Check if message contains thinking section
        const thinkMatch = message.text.match(/<think>([\s\S]*?)<\/think>/);
        
        if (!thinkMatch) {
            if (message.text === 'Gale is reading your message...') {
                return (<><span>Gale is reading your message </span><TypingDots /></>);
            }
            if (message.text === 'Gale is now writing a response...') {
                return (<><span>Gale is now writing a response </span><TypingDots /></>);
            }
            if (message.text === 'Gale is writing a response...') {
                return (<><span>Gale is writing a response </span><TypingDots /></>);
            }
            if (message.text === 'Gale is writing a message...') {
                return (<><span>Gale is writing a message </span><TypingDots /></>);
            }
            return message.text;
        }
        
        const thinkContent = thinkMatch[1];
        const mainContent = message.text.replace(/<think>[\s\S]*?<\/think>/, '').trim();
        // Default to collapsed (true) if the state isn't explicitly set to false
        const isCollapsed = collapsedThinking[message.id] !== undefined 
            ? collapsedThinking[message.id] 
            : true;
        
        return (
            <>
                <div className="thinking-section" style={{ marginBottom: '10px', display: 'none' }}>
                    <div 
                        className="thinking-header" 
                        onClick={() => toggleThinking(message.id)}
                        style={{
                            backgroundColor: '#3a3a3a',
                            padding: '6px 10px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            marginBottom: isCollapsed ? '0' : '8px',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        <span style={{ fontSize: '13px', marginRight: '8px' }}>
                            {isCollapsed ? '▶' : '▼'} My thought process
                        </span>
                    </div>
                    
                    {!isCollapsed && (
                        <div 
                            className="thinking-content"
                            style={{
                                backgroundColor: '#333',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                fontSize: '13px',
                                color: '#ccc',
                                maxHeight: '200px',
                                overflowY: 'auto'
                            }}
                        >
                            {thinkContent}
                        </div>
                    )}
                </div>
                {mainContent}
            </>
        );
    };

    useEffect(() => {
        if (chatWith) {
            // Chat type has been selected
            createInitialMessage();
        }
    }, [chatWith]);

    useEffect(() => {
        scrollToBottom();
        
        // Don't save if AI is currently streaming
        if (isAIStreaming) {
            return;  // Skip saving during streaming
        }
        
        // Save conversation to localStorage (excluding system messages and status indicators)
        const conversationToSave = messages.filter(msg => {
            // Only include Gale and You messages
            if (msg.sender !== 'Gale' && msg.sender !== 'You') {
                return false;
            }
            
            // Exclude status messages (loading indicators)
            const isStatusMessage = 
                msg.text.includes('Gale is writing') ||
                msg.text.includes('Gale is reading') ||
                msg.text.includes('Gale is thinking') ||
                msg.text.includes('Gale is typing');
            
            return !isStatusMessage;
        });
        
        if (conversationToSave.length > 0 && userId) {
            try {
                const storageKey = `conversation_${userId}`;
                
                // Get existing conversation history
                let existingHistory = [];
                try {
                    const stored = localStorage.getItem(storageKey);
                    if (stored) {
                        existingHistory = JSON.parse(stored);
                        if (!Array.isArray(existingHistory)) {
                            existingHistory = [];
                        }
                    }
                } catch (parseError) {
                    console.log('Failed to parse existing history:', parseError);
                    existingHistory = [];
                }
                
                // Create a Set of existing message IDs to avoid duplicates
                const existingIds = new Set(existingHistory.map(msg => msg.id));
                
                // Filter new messages that aren't already in history and strip avatar
                const newMessages = conversationToSave
                    .filter(msg => !existingIds.has(msg.id))
                    .map(msg => ({
                        id: msg.id,
                        text: msg.text,
                        sender: msg.sender
                        // Intentionally omitting avatar field to save space
                    }));
                
                // Filter out consecutive duplicate messages
                let filteredNewMessages = [];
                let lastMessage = existingHistory.length > 0 ? existingHistory[existingHistory.length - 1] : null;

                for (const newMsg of newMessages) {
                    // Check if this message is identical to the last message (same text and sender)
                    if (!lastMessage || lastMessage.text !== newMsg.text || lastMessage.sender !== newMsg.sender) {
                        filteredNewMessages.push(newMsg);
                        lastMessage = newMsg;
                    }
                    // If it is identical to the previous message, skip it (don't add to filteredNewMessages)
                }
                
                // Apply AI message replacement logic to prevent greeting accumulation
                let mergedConversation = [...existingHistory];
                
                for (const newMsg of filteredNewMessages) {
                    if (mergedConversation.length === 0) {
                        // First message in history, always add
                        mergedConversation.push(newMsg);
                    } else if (newMsg.sender === 'You') {
                        // Human message, always add
                        mergedConversation.push(newMsg);
                    } else if (newMsg.sender === 'Gale') {
                        // AI message - check if we should replace the last AI message
                        const lastMsg = mergedConversation[mergedConversation.length - 1];
                        if (lastMsg.sender === 'Gale') {
                            // Last message was also from AI, replace it (removes page greeting accumulation)
                            mergedConversation[mergedConversation.length - 1] = newMsg;
                        } else {
                            // Last message was from human, add this AI message normally
                            mergedConversation.push(newMsg);
                        }
                    } else {
                        // Other message types (like 'System'), add normally
                        mergedConversation.push(newMsg);
                    }
                }
                
                // Optional: Keep only the last 100 messages to prevent unlimited growth
                const conversationToStore = mergedConversation.slice(-100);
                
                // Save the merged conversation
                localStorage.setItem(storageKey, JSON.stringify(conversationToStore));
            } catch (error) {
                console.log('Failed to save conversation to localStorage:', error);
            }
        }
    }, [messages, userId, isAIStreaming]);  // Add isAIStreaming to dependencies

    useEffect(() => {
        if (textareaRef.current) {
            adjustTextareaHeight(textareaRef.current);
        }
    }, [newMessage]);

    useEffect(() => {
        loadAvatars();
    }, [topic]);

    useEffect(() => {
        if (avatarsLoaded) {
            createInitialMessage();
        }
    }, [avatarsLoaded]);

    useEffect(() => {
		context.setAppHeaderNone(true);
		context.setAppSidebarNone(true);
		context.setAppThemePanelNone(true);
		context.setAppContentClass('p-0');

        textareaRef.current.focus();
		
		return function cleanUp() {
			if (waitingTimeoutRef.current) {
				clearTimeout(waitingTimeoutRef.current);
			}
			context.setAppHeaderNone(false);
			context.setAppSidebarNone(false);
			context.setAppThemePanelNone(false);
			context.setAppContentClass('');
		};
    }, []);

    // One-time handshake: notify parent widget that iframe is ready to receive pageContext
    useEffect(() => {
        try {
            if (window.parent && window.parent !== window) {
                const parentOrigin = document.referrer ? new URL(document.referrer).origin : '*';
                window.parent.postMessage({ type: 'gfa:ready' }, parentOrigin);
            }
        } catch (e) {
            try {
                window.parent?.postMessage({ type: 'gfa:ready' }, '*');
            } catch (_) {}
        }
    }, []);

    // Timeout fallback to ensure streaming state resets if "llm streaming complete" message is missed
    useEffect(() => {
        if (isAIStreaming) {
            // Fallback: Reset streaming after 30 seconds max
            const timeout = setTimeout(() => {
                setIsAIStreaming(false);
            }, 30000);
            
            return () => clearTimeout(timeout);
        }
    }, [isAIStreaming]);

    // PostMessage receiver for page content from parent window
    useEffect(() => {
        const handleMessage = (event) => {
            // GRACEFUL DEGRADATION: If origin not in allowlist, chatbot works normally
            if (!isAllowedOrigin(event.origin)) {
                // Origin not in allowlist, no page context available
                // No error - chatbot continues to work without page context
                return;
            }

            // console.log('Received page content:', event.data);
            // console.log('Received page content type:', event.data?.type);
            
            // Validate message structure
            if (event.data?.type === 'pageContent') {
                setPageContent(event.data);
                
                // Update the existing system message with page context; ensure we add the first Gale message
                // without flashing a canned greeting on non-home pages.
                setMessages(prevMessages => {
                    const updatedMessages = [...prevMessages];
                    const systemMessageIndex = updatedMessages.findIndex(msg => msg.sender === 'System');
                    
                    if (systemMessageIndex >= 0) {
                        updatedMessages[systemMessageIndex] = {
                            ...updatedMessages[systemMessageIndex],
                            pageContext: event.data
                        };
                    }
                    
                    const isHome = isParentHomeUrl(event.data.url);
                    const firstGaleIndex = updatedMessages.findIndex(msg => msg.sender === 'Gale');
                    if (firstGaleIndex === -1) {
                        // No Gale message yet; add the appropriate first message now
                        updatedMessages.push({
                            id: Date.now(),
                            text: isHome ? chatTopicData.initial_message : 'Gale is writing a message...',
                            sender: 'Gale',
                            avatar: galeAvatar
                        });
                    } else {
                        // Do not swap text here; backend greeting will replace it. Minimal logic for stability.
                    }

                    return updatedMessages;
                });
                
                // Fire bootstrap immediately upon receiving page context to start LLM ASAP
                if (!initialRequestSentRef.current) {
                    try {
                        // Claim the bootstrap send synchronously to avoid any duplicate send from fallbacks or later events
                        initialRequestSentRef.current = true;
                        // Mark that AI is starting to stream
                        setIsAIStreaming(true);
                        const conversationHistory = getStoredConversationHistory();
                        const bootstrapSystemMessage = {
                            id: userId,
                            text: 'User ID',
                            sender: 'System',
                            topic: topic,
                            pageContext: event.data,
                            conversationHistory: conversationHistory || [],  // Send empty array instead of null
                            purpose: 'bootstrap'
                        };
                        fetch('https://3p5i2ef2f3.execute-api.us-east-2.amazonaws.com/aichatbotchatrequest', {
                            method: 'POST',
                            headers: {
                                'Accept': 'application/json',
                                'Authorization': Config.aichatbotchatrequest,
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify([bootstrapSystemMessage])
                        }).catch(() => {
                            setIsAIStreaming(false);  // Reset on error
                        });
                    } catch (_) {
                        setIsAIStreaming(false);  // Reset on error
                        // If anything throws synchronously, keep the flag set to prevent cascaded retries
                    }
                }
                
                // Successfully received page context data
                // Includes page URL, title, main text, and headings
                // This will enhance the AI's contextual understanding
            }
        };
        
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, []);

	return (
		<div style={{ minHeight: '100vh', background: theme?.effects?.containerBg || 'transparent' }}>
			<div 
				className="chat-container"
				style={{ 
					color: 'white', 
					display: 'flex', 
					flexDirection: 'column', 
					justifyContent: 'space-between', // This will push your message container and form to opposite ends
					minHeight: '95.0vh', // 93.0vh 82.0vh or try `height: 100%` to make it flexible
					alignItems: 'center', // This will center everything horizontally
					padding: '20px', // Add padding to the entire container if needed
					// Enhanced background styling for both themes
					backgroundImage: theme.background ? `url(${theme.background})` : 'none',
					background: 'none',
					backgroundSize: 'cover',
					backgroundPosition: 'center',
					backgroundAttachment: 'fixed',
					// Enhanced shadow effects
					boxShadow: 'none',
					// Border effects for patriotic theme
					borderTop: 'none',
					borderImage: 'none'
				}}
				>
				<style>
					{`
						.responsive-div {
							width: 50%; /* default width */
							align-items: center;
							// margin-bottom: 20px;
						}
							
						@media (max-width: 768px) {
							.responsive-div {
								width: 100%;
							}
						}


						.avatar-modal {
							position: fixed;
							top: 0;
							left: 0;
							width: 100%;
							height: 100%;
							background-color: rgba(0, 0, 0, 0.8);
							z-index: 9999;
							display: flex;
							align-items: center;
							justify-content: center;
							animation: fadeIn 0.3s ease;
						}

						.avatar-modal img {
							max-width: 90%;
							max-height: 90%;
							border-radius: 20px;
							box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
							animation: scaleIn 0.3s ease;
						}

						@keyframes fadeIn {
							from { opacity: 0; }
							to { opacity: 1; }
						}

						@keyframes scaleIn {
							from { transform: scale(0.8); }
							to { transform: scale(1); }
						}


					`}
				</style>

				<div 
					className="responsive-div" 
					style={{ 
						maxHeight: '100%', 
						overflow: 'auto',
						WebkitOverflowScrolling: 'touch', // iOS smooth scrolling
					}}
					>

					{isInIframe ? null : <><br /><br /></>}

					<div>
                        {isInIframe ? (
                            <h3 style={{ 
                                marginBottom: '15px',
                                fontSize: theme?.typography?.headerSize || '18px',
                                background: theme?.special?.headerGradient || theme?.gradient || 'linear-gradient(to right, #00ffff 0%, #ff00ff 15%, #ed521f 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                color: 'transparent',
                                fontFamily: theme?.typography?.fontFamily || 'Arial, sans-serif',
                                fontWeight: theme?.typography?.weight || 'bold',
                                // Epic patriotic shadow effects
                                textShadow: theme?.special?.shadowEffect ? '0 0 10px rgba(255, 215, 0, 0.3), 0 0 20px rgba(255, 255, 255, 0.2)' : 'none',
                                letterSpacing: '1px'
                            }}>
                                <b>{pageHeader}</b>
                            </h3>
                        ) : (
                            <h3 style={{ 
                                marginBottom: '35px',
                                fontSize: theme?.typography?.headerSize || '18px',
                                background: theme?.special?.headerGradient || theme?.gradient || 'linear-gradient(to right, #00ffff 0%, #ff00ff 15%, #ed521f 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                color: 'transparent',
                                fontFamily: theme?.typography?.fontFamily || 'Arial, sans-serif',
                                fontWeight: theme?.typography?.weight || 'bold',
                                textShadow: theme?.special?.shadowEffect ? '0 0 10px rgba(255, 215, 0, 0.3), 0 0 20px rgba(255, 255, 255, 0.2)' : 'none',
                                letterSpacing: '1px'
                            }}>
                                <b>{pageHeader}</b>
                            </h3>
                        )}
                    </div>

					{messages.filter(message => message.sender === 'Gale' || message.sender === 'You').map((message) => (
						<div key={message.id} style={{ display: 'flex', alignItems: 'flex-start', margin: '10px 0' }}>
							<img 
								src={message.avatar}
								alt="Avatar"
								onClick={() => handleAvatarClick(message.avatar)}
								style={{
									width: '50px',
									height: '50px',
									borderRadius: theme?.layout?.avatarRadius || '6px', // Make the image square with rounded corners.
									// Enhanced avatar styling with futuristic glow or patriotic effects
									border: theme?.effects?.neonBorder || (theme?.special?.starBorder ? `3px solid ${theme.colors?.starGold || '#FFD700'}` : 'none'),
									boxShadow: theme?.effects?.avatarGlow || (theme?.special?.shadowEffect ? `0 0 15px rgba(255, 215, 0, 0.4), 0 0 30px rgba(178, 34, 52, 0.2)` : 'none'),
									objectFit: 'cover', // Ensure the image covers the area.
									marginRight: '10px', // Add some space between the avatar and the message.
                                    marginTop: '7px',
                                    cursor: 'pointer'
								}}
							/>
							<div style={{ 
								flex: 1, 
								fontSize: theme?.typography?.bodySize || '15px', 
								fontFamily: theme?.typography?.fontFamily || 'Arial, sans-serif', 
								lineHeight: '1.8', 
								whiteSpace: 'pre-wrap' 
							}}>
                                <span style={{ fontWeight: 'bold' }}>{message.sender}:</span><br />{renderMessageText(message)}
							</div>
						</div>
					))}
					<div ref={messagesEndRef} />
				</div>

				<div className="responsive-div" style={{ display: 'flex', alignItems: 'center', marginBottom: '3px', position: 'relative' }}>
                    
                    {topic.includes('-') &&
                        <select 
                            value={chatWith}
                            onChange={handleChatWithChange}
                            style={{
                                height: '48px',
                                borderRadius: theme?.layout?.selectRadius || '18px',
                                padding: '0 15px',
                                outline: 'none',
                                // Enhanced select styling with futuristic or patriotic effects
                                background: theme?.special?.inputGradient || theme?.colors?.primary || '#525252',
                                backgroundColor: theme?.special?.inputGradient ? 'transparent' : (theme?.colors?.primary || '#525252'),
                                color: theme?.colors?.text || 'white',
                                border: theme?.effects?.neonBorder || (theme?.special?.starBorder ? `2px solid ${theme.colors?.starGold || '#FFD700'}` : 'none'),
                                boxShadow: theme?.effects?.inputGlow || (theme?.special?.shadowEffect ? '0 4px 15px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.1)' : 'none'),
                                fontSize: '1rem',
                                fontFamily: theme?.typography?.fontFamily || 'Arial, sans-serif',
                                paddingRight: '30px', // Space for the custom dropdown arrow
                            }}
                        >
                            <option>CRM Notes</option>
                            <option>Call</option>
                        </select>
                    }

					{/* Theme-safe variables for textarea styling */}
					{(() => {
						const radius = theme?.layout?.borderRadius || '18px';
						// Use special inputGradient for patriotic theme, gradient background for futuristic, or fallback solid color
						const bgColor = theme?.special?.inputGradient || theme?.colors?.primary || '#525252';
						const textColor = theme?.colors?.inputText || 'white';
						const fontFamily = theme?.typography?.fontFamily || 'Arial, sans-serif';
						const fontSize = theme?.typography?.bodySize || '15px';
						// Enhanced effects for futuristic theme
						const inputShadow = theme?.effects?.inputGlow || (theme?.special?.shadowEffect ? '0 4px 15px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.1)' : 'none');
						const inputBorder = theme?.effects?.neonBorder || (theme?.special?.starBorder ? `2px solid ${theme.colors?.starGold || '#FFD700'}` : 'none');
						
						return (
					<textarea
						ref={textareaRef}
						value={newMessage}
						maxLength='600'
						onChange={handleMessageChange}
						onKeyDown={(e) => {
							if (e.key === 'Enter' && !e.shiftKey) { // Checks if Enter key is pressed without the Shift key
								e.preventDefault(); // Prevents the default action to avoid a new line in the textarea
								handleSendMessage(); // Calls the function to send the message
							}
						}}
						style={{ 
							flexGrow: 1,
							boxSizing: 'border-box', // This ensures padding is included in the height calculation
							minHeight: '48px',
							maxHeight: '120px',
							height: 'auto',
							resize: 'none',
							borderRadius: `${radius} 0 0 ${radius}`,
							padding: '14.1px 10px', // Adjusted padding to vertically center the text
							border: 'none',
                            marginLeft: '10px',
							marginRight: '-1px',
							outline: 'none',
							// Enhanced background with gradient support
							background: bgColor.includes('gradient') ? bgColor : 'none',
							backgroundColor: bgColor.includes('gradient') ? 'transparent' : bgColor,
							// Enhanced border and shadow effects
							border: inputBorder,
							boxShadow: inputShadow,
							color: textColor,
							fontSize: fontSize,
							fontFamily: fontFamily,
                            lineHeight: '20px', // Added line-height to help with vertical centering
							overflowY: 'hidden', // Hide vertical scrollbar
						}}
						placeholder="Message Gale..."
					/>
						);
					})()}
					{/* Theme-safe variables for send button styling */}
					{(() => {
						const radius = theme?.layout?.borderRadius || '18px';
						// Use epic buttonGradient for patriotic theme, gradient for futuristic, or solid color fallback
						const bgColor = theme?.special?.buttonGradient || theme?.colors?.secondary || '#4a4a4a';
						// Enhanced effects for futuristic theme
						const buttonShadow = theme?.effects?.buttonGlow || (theme?.special?.shadowEffect ? 
							'0 6px 20px rgba(178, 34, 52, 0.4), 0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.2)' : 'none');
						const buttonBorder = theme?.effects?.neonBorder || (theme?.special?.starBorder ? `2px solid ${theme.colors?.starGold || '#FFD700'}` : 'none');
						const buttonTextShadow = theme?.effects?.textGlow || (theme?.special?.shadowEffect ? '0 1px 2px rgba(0, 0, 0, 0.7)' : 'none');
						
						return (
					<button 
						onClick={handleSendMessage} 
						style={{ 
							flexGrow: 0, // Prevent the button from growing
							boxSizing: 'border-box', // This ensures padding is included in the height calculation
							height: `${textareaHeight}px`, // Match height with textarea
							borderRadius: `0 ${radius} ${radius} 0`, // Rounded corners on the right side only
							// Enhanced button styling with gradient support
							background: bgColor.includes('gradient') ? bgColor : 'none',
							backgroundColor: bgColor.includes('gradient') ? 'transparent' : bgColor,
							// Enhanced shadow, border and text effects
							border: buttonBorder,
							boxShadow: buttonShadow,
							fontWeight: theme?.typography?.weight || 'normal',
							color: 'white',
							textShadow: '0 0 6px rgba(13, 71, 161, 0.8), 0 1px 2px rgba(0, 0, 0, 0.7)',
							fontSize: '1rem',
							padding: '0 20px',
							marginLeft: '-1px', // Aligns seamlessly with the textarea
							whiteSpace: 'nowrap', // Keeps the text within the button on one line
							alignSelf: 'stretch', // Stretch to match parent's height
						}}
						>
						Send
					</button>
						);
					})()}
                    <button
                        onClick={isRecording ? stopRecording : startRecording}
                        className="btn btn-primary"
                        disabled={submitDisabled}
                        style={{ 
                            flexGrow: 0, // Prevent the button from growing
                            boxSizing: 'border-box', // This ensures padding is included in the height calculation
                            height: `${textareaHeight}px`, // Match height with textarea
                            width: `${textareaHeight}px`, // Keep it square based on height
                            borderRadius: '50%', // Make the button circular
                            // Epic patriotic microphone button styling
                            background: theme?.special?.buttonGradient || theme?.colors?.secondary || '#4a4a4a',
                            backgroundColor: theme?.special?.buttonGradient ? 'transparent' : (theme?.colors?.secondary || '#4a4a4a'),
                            border: theme?.special?.goldAccents ? `2px solid ${theme.colors?.starGold || '#FFD700'}` : 'none',
                            boxShadow: theme?.special?.shadowEffect ? '0 4px 15px rgba(178, 34, 52, 0.4), 0 2px 8px rgba(0, 0, 0, 0.3)' : 'none',
                            color: 'white', // Match the text color
                            display: 'none', // Hidden microphone button (was 'flex' to center the icon)
                            justifyContent: 'center', // Center the icon horizontally
                            alignItems: 'center', // Center the icon vertically
                            marginLeft: '10px', // Space between the two buttons
                            alignSelf: 'stretch', // Stretch to match parent's height
                        }}
                        >
                        {isRecording ? <i className="fas fa-stop-circle"></i> : <i className="fas fa-microphone"></i>}
                    </button>
					
					{/* Powered by text - positioned absolutely */}
					<div style={{ 
						position: 'absolute',
						bottom: isInIframe ? '-25px' : '-43px',
						left: '50%',
						transform: 'translateX(-50%)',
						textAlign: 'center', 
						fontSize: '11px',
						color: '#888',
						whiteSpace: 'nowrap'
					}}>
						Powered by{' '}
						<a 
							href="https://galeforce.ai/" 
							target="_blank" 
							rel="noopener noreferrer"
							style={{ 
								color: '#aaa', 
								textDecoration: 'none',
								fontSize: '11px'
							}}
							onMouseEnter={(e) => e.target.style.color = '#ccc'}
							onMouseLeave={(e) => e.target.style.color = '#aaa'}
						>
							Gale Force AI
						</a>
					</div>
				</div>
        	</div>

			{showAvatarModal && (
				<div className="avatar-modal" onClick={handleModalClose}>
					<img src={modalAvatarSrc} alt="Full size avatar" />
				</div>
			)}
		</div>
	)
}

export default AIChatbot;